import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Toast Notification System - Modern toast notifications for user feedback
 * Supports success, error, info, and warning types with auto-dismiss
 */

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

const Toast = ({ id, type = 'info', title, message, duration, onClose }) => {
  const icons = {
    success: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
    error: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
    info: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    ),
    warning: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    ),
  };

  const colors = {
    success: { icon: '#10b981', border: '#059669' },
    error: { icon: '#ef4444', border: '#dc2626' },
    info: { icon: '#3b82f6', border: '#2563eb' },
    warning: { icon: '#f59e0b', border: '#d97706' },
  };

  const Icon = icons[type];
  const color = colors[type];

  return (
    <div 
      className="toast-item"
      style={{
        pointerEvents: 'all',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        minWidth: '320px',
        maxWidth: '420px',
        background: '#fff',
        borderRadius: '12px',
        padding: '14px 16px',
        boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px ${color.border}`,
        animation: 'toastSlideIn 0.3s cubic-bezier(0.21, 1.02, 0.73, 1)',
        transition: 'all 0.2s ease'
      }}
    >
      <div 
        className="toast-icon"
        style={{
          flexShrink: 0,
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color.icon
        }}
      >
        {Icon}
      </div>
      <div 
        className="toast-content"
        style={{
          flex: 1,
          minWidth: 0
        }}
      >
        {title && (
          <div 
            className="toast-title"
            style={{
              fontWeight: 600,
              fontSize: '14px',
              color: '#111827',
              marginBottom: '2px'
            }}
          >
            {title}
          </div>
        )}
        {message && (
          <div 
            className="toast-message"
            style={{
              fontSize: '13px',
              color: '#6b7280',
              lineHeight: 1.5
            }}
          >
            {message}
          </div>
        )}
      </div>
      <button 
        className="toast-close"
        onClick={() => onClose(id)}
        aria-label="Close notification"
        style={{
          flexShrink: 0,
          background: 'transparent',
          border: 'none',
          color: '#9ca3af',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: '6px',
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
};

Toast.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
  title: PropTypes.string,
  message: PropTypes.string,
  duration: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = 'info', title, message, duration = 5000 }) => {
    const id = Date.now() + Math.random();
    
    setToasts(prev => [...prev, { id, type, title, message, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toast = useCallback({
    success: (title, message, duration) => addToast({ type: 'success', title, message, duration }),
    error: (title, message, duration) => addToast({ type: 'error', title, message, duration }),
    info: (title, message, duration) => addToast({ type: 'info', title, message, duration }),
    warning: (title, message, duration) => addToast({ type: 'warning', title, message, duration }),
  }, [addToast]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div 
        className="toast-container"
        style={{
          position: 'fixed',
          top: 'calc(64px + 20px)',
          right: '20px',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          pointerEvents: 'none'
        }}
      >
        {toasts.map(t => (
          <Toast 
            key={t.id}
            id={t.id}
            type={t.type}
            title={t.title}
            message={t.message}
            duration={t.duration}
            onClose={removeToast}
          />
        ))}
      </div>

      <style>
        {`
          @keyframes toastSlideIn {
            from {
              transform: translateX(400px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          .toast-item:hover {
            transform: translateX(-4px) scale(1.02);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
          }

          .toast-close:hover {
            background: #f3f4f6 !important;
            color: #111827 !important;
            transform: scale(1.1);
          }

          .toast-close:active {
            transform: scale(0.95);
          }

          @media (max-width: 768px) {
            .toast-container {
              right: 12px !important;
              left: 12px !important;
              top: calc(64px + 12px) !important;
            }
            
            .toast-item {
              min-width: 0 !important;
              max-width: 100% !important;
            }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideDown {
            from { 
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to { 
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }

          @keyframes tooltipFadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }

          .command-palette-input::placeholder {
            color: #9ca3af;
          }

          .command-palette-content::-webkit-scrollbar { 
            width: 8px; 
          }
          
          .command-palette-content::-webkit-scrollbar-thumb {
            background: #d1d5db;
            border-radius: 999px;
          }
          
          .command-palette-content::-webkit-scrollbar-thumb:hover {
            background: #9ca3af;
          }

          .breadcrumbs-link:hover {
            color: #3b82f6;
            transform: translateY(-1px);
          }

          .breadcrumbs-link:active {
            transform: translateY(0);
          }

          .breadcrumbs-link:focus-visible {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
            border-radius: 4px;
          }

          .dark-mode .breadcrumbs {
            background: var(--bg-secondary, #1e293b);
            border-color: var(--border-color, #374151);
          }

          .dark-mode .breadcrumbs-link,
          .dark-mode .breadcrumbs-text {
            color: var(--text-secondary, #d1d5db);
          }

          .dark-mode .breadcrumbs-current {
            color: var(--text-primary, #f9fafb);
          }

          .dark-mode .breadcrumbs-link:hover {
            color: #60a5fa;
          }

          .dark-mode .command-palette {
            background: var(--bg-secondary, #1e293b);
            border: 1px solid var(--border-color, #374151);
          }

          .dark-mode .command-palette-input {
            color: var(--text-primary, #f9fafb);
          }

          .dark-mode .command-palette-item-label {
            color: var(--text-primary, #f9fafb);
          }

          .dark-mode .command-palette-item-description {
            color: var(--text-secondary, #d1d5db);
          }

          .dark-mode .command-palette-item:hover,
          .dark-mode .command-palette-item.selected {
            background: rgba(255, 255, 255, 0.05);
          }

          .dark-mode .toast-item {
            background: var(--bg-secondary, #1e293b);
            border-color: var(--border-color, #374151);
          }

          .dark-mode .toast-title {
            color: var(--text-primary, #f9fafb);
          }
        `}
      </style>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ToastProvider;
